import React, {useState, useEffect} from 'react';
import styles from './tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../configs/config";

const PurchaseRecordsTable = () => {
    const [purchaseData, setPurchaseData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        const fetchPurchaseData = async () => {
            try {
                const response = await fetch(`${PRISMAX_BACKEND_URL}/api/get-purchase_records`);
                if (!response.ok) {
                    throw new Error('Failed to fetch purchase records');
                }
                const data = await response.json();
                if (data.success && data.data) {
                    setPurchaseData(data.data);
                    setTotalPages(Math.ceil(data.data.length / itemsPerPage));
                } else {
                    throw new Error('Invalid data format');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPurchaseData();
    }, []);

    // Get current page items
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = purchaseData.slice(indexOfFirstItem, indexOfLastItem);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Go to previous page
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Format currency display
    const formatCurrency = (amount, currency) => {
        if (currency === 'usd') {
            return `${amount / 100}`;
        } else {
            return `${amount}`;
        }

    };

    // Format wallet address (show first 6 and last 4 characters)
    const formatWalletAddress = (address) => {
        if (!address) return 'N/A';
        if (address.length <= 10) return address;
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    // Format transaction hash (show first 8 and last 6 characters)
    const formatTransactionHash = (hash) => {
        if (!hash) return 'N/A';
        if (hash.length <= 14) return hash;
        return `${hash.slice(0, 8)}...${hash.slice(-6)}`;
    };

    if (loading) {
        return <div className={styles.loading}>Loading purchase records...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <h2 className={styles.tableTitle}>Purchase Records</h2>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Checkout Session</th>
                        <th>Payment Intent</th>
                        <th>User ID</th>
                        <th>Email</th>
                        <th>Wallet Address</th>
                        <th>Amount</th>
                        <th>Currency</th>
                        <th>Payment Status</th>
                        <th>Crypto Transaction Hash</th>
                        <th>Created At</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.length > 0 ? (
                        currentItems.map((item) => (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>{item.stripe_checkout_session_id || 'N/A'}</td>
                                <td>{item.stripe_payment_intent_id || 'N/A'}</td>
                                <td>{item.user_id || 'N/A'}</td>
                                <td>{item.email_address}</td>
                                <td title={item.wallet_address}>
                                    {formatWalletAddress(item.wallet_address)}
                                </td>

                                <td>{formatCurrency(item.amount_total, item.currency)}</td>

                                <td>{item.currency?.toUpperCase()}</td>
                                <td>
                    <span className={`${styles.statusBadge} ${styles[item.payment_status?.toLowerCase()]}`}>
                      {item.payment_status}
                    </span>
                                </td>
                                <td title={item.solana_transaction_hash}>
                                    {item.solana_transaction_hash}
                                </td>
                                <td>{new Date(item.created_at).toLocaleString()}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="12" className={styles.noData}>
                                No purchase records found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {purchaseData.length > 0 && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            // Show limited page numbers with ellipsis for better UX
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default PurchaseRecordsTable;